import { create } from 'zustand';

import type { AttachedFile, SupportCategoryCode, SupportErrorKind } from '../types';
import { dropDuplicates } from '../utils/validate-attachments';

export interface SubmitError {
  kind: SupportErrorKind;
  /** Names of the refused files (kind === 'files_rejected'). */
  fileNames: string[];
}

interface SupportDraftState {
  category: SupportCategoryCode | '';
  description: string;
  timePeriod: string;
  files: AttachedFile[];
  /** Ids of files the server refused; they are not sent again. */
  rejectedFileIds: string[];
  sending: boolean;
  error: SubmitError | null;
  /**
   * Incremented on every reset. A request remembers the epoch it started in and
   * drops its outcome if the draft was reset meanwhile (e.g. another user signed in).
   */
  epoch: number;

  setCategory: (category: SupportCategoryCode) => void;
  setDescription: (value: string) => void;
  setTimePeriod: (value: string) => void;
  addFiles: (files: File[]) => void;
  removeFile: (id: string) => void;
  beginSend: () => void;
  failSend: (error: SubmitError) => void;
  clearError: () => void;
  /** Wipes the whole draft (on HTTP 202 and on log out / user change). */
  reset: () => void;
}

const INITIAL = {
  category: '' as const,
  description: '',
  timePeriod: '',
  files: [] as AttachedFile[],
  rejectedFileIds: [] as string[],
  sending: false,
  error: null as SubmitError | null,
};

let fileCounter = 0;

/**
 * In-memory draft of the support request. Closing the dialog keeps it, so the
 * user can come back to it; it is cleared on a successful submit (202) and
 * when the signed-in user changes or logs out. Deliberately not persisted:
 * the text may hold sensitive details and `File` objects cannot be serialized.
 */
export const useSupportDraftStore = create<SupportDraftState>()((set) => ({
  ...INITIAL,
  epoch: 0,

  setCategory: (category) => {
    set({ category });
  },
  setDescription: (description) => {
    set({ description });
  },
  setTimePeriod: (timePeriod) => {
    set({ timePeriod });
  },

  addFiles: (incoming) => {
    set((state) => {
      const fresh = dropDuplicates(
        state.files.map((f) => f.file),
        incoming,
      );
      if (fresh.length === 0) return state;
      return {
        files: [
          ...state.files,
          ...fresh.map((file) => ({ id: `support-file-${String(++fileCounter)}`, file })),
        ],
      };
    });
  },
  removeFile: (id) => {
    set((state) => ({
      files: state.files.filter((f) => f.id !== id),
      rejectedFileIds: state.rejectedFileIds.filter((r) => r !== id),
    }));
  },

  beginSend: () => {
    set({ sending: true, error: null });
  },
  failSend: (error) => {
    set((state) => {
      const rejected =
        error.kind === 'files_rejected'
          ? state.files.filter((f) => error.fileNames.includes(f.file.name)).map((f) => f.id)
          : [];
      return {
        sending: false,
        error,
        rejectedFileIds: [...new Set([...state.rejectedFileIds, ...rejected])],
      };
    });
  },
  clearError: () => {
    set({ error: null });
  },

  reset: () => {
    set((state) => ({ ...INITIAL, epoch: state.epoch + 1 }));
  },
}));
